import { FC, memo, useEffect, useMemo, useState } from "react";
//@ts-ignore
import { useTable, usePagination, useExpanded, useFilters } from "react-table";
import { useTranslation } from "react-i18next";
import "./style.css";
import { Icon } from "@iconify/react/dist/iconify.js";
import { ITable } from "./Table.type";
import { ActionsId } from "src/common/constants";

const PaggingScale = 5;

const Table: FC<ITable> = ({
  columns,
  data = [],
  fetchData,
  TotalRows,
  hiddenColumns = [],
  tableFiller,
  assignRelatedClass,
  askForFetch,
  isPagination = true,
  setTableFiller,
  hideActions,
  exportExcel,
  renderRowSubComponent
}) => {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [paginationPages, setPaginationPages] = useState<number[]>();
  const [expanded, setExpanded] = useState<any>({}); // 👈 controlled expanded state

  const handleHide = useMemo(() => {
    let arr = [];
    if (hideActions) {
      arr.push("actions");
    }
    if (hiddenColumns) {
      arr.push(...hiddenColumns);
    }
    arr.push("");
    return arr;
  }, [hideActions, hiddenColumns]);

  const {
    getTableProps,
    getTableBodyProps,
    prepareRow,
    headerGroups,
    page,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    gotoPage,
    state: { pageIndex, pageSize, filters }
  } = useTable(
    {
      columns,
      data,
      pageCount: Math.ceil(TotalRows / tableFiller?.PageSize),
      initialState: {
        pageIndex: 0,
        pageSize: tableFiller?.PageSize || 20,
        hiddenColumns: handleHide
      },
      defaultColumn: { Filter: "" },
      manualPagination: true,
      manualFilters: true,
      expanded,
      onExpandedChange: (newExpanded: any) => {
        const keys = Object.keys(newExpanded);

        if (keys.length === 0) {
          // all closed
          setExpanded({});
        } else {
          // 👇 only keep the last toggled row open
          const lastKey = keys[keys.length - 1];
          setExpanded({ [lastKey]: true });
        }
      }
    },
    useFilters,
    useExpanded,
    usePagination
  );

  useEffect(() => {
    if (isPagination && askForFetch && fetchData) {
      fetchData({ pageIndex, pageSize, filters });
    }
  }, [fetchData, tableFiller?.page, pageSize, isPagination, filters]);

  const handleExportExcel = () => {
    setIsLoading(true);
    exportExcel &&
      exportExcel(filters, i18n.language)
        .then(() => setIsLoading(false))
        .catch(() => setIsLoading(false));
  };

  useEffect(() => {
    let tempArr: any[] = [];
    if (TotalRows / tableFiller?.PageSize >= 0) {
      for (var i = 2; i <= Math.ceil(TotalRows / tableFiller?.PageSize); i++) {
        tempArr.push(i);
      }
      setPaginationPages((prev: any) => [...tempArr]);
    }
  }, [TotalRows, tableFiller?.PageSize]);

  const handleNextPage = () => {
    setTableFiller &&
      setTableFiller((e: any) => ({ ...e, page: e.page + 1 }));
    nextPage();
  };

  const handleGoToPage = (PageIndex: number) => {
    setTableFiller &&
      setTableFiller((e: any) => ({ ...e, page: PageIndex }));
    gotoPage(PageIndex);
  };

  const handlePrevPage = () => {
    setTableFiller &&
      setTableFiller((e: any) => ({ ...e, page: e.page - 1 }));
    previousPage();
  };

  return (
    <div className="TableContainer">
      {exportExcel ? (
        <button
          className="btnTableRelevant"
          onClick={handleExportExcel}
          disabled={isLoading}
        >
          <span
            className={isLoading ? "spinner-border spinner-border-sm" : ""}
            role="status"
            aria-hidden="true"
          ></span>
          {t("Export")}
        </button>
      ) : null}

      <div className="pagination">
        {TotalRows ? (
          <div className="displayedRowsCount">
            {`${TotalRows ? 1 + pageSize * tableFiller?.page : 0
              } ${t("to")} ${TotalRows == 0
                ? 0
                : Math.min(pageSize * (tableFiller?.page + 1), TotalRows)
              } ${t("row")} ${t("of")} ${TotalRows} ${t("row")}`}
          </div>
        ) : (
          ""
        )}
      </div>

      <div className="wrapperTable">
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup: any) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column: any) => (
                  <th
                    {...column.getHeaderProps()}
                    className={`${column.id === ActionsId ? "stickyCol" : ""} ${column?.visible === false ? "d-none" : ""
                      }`}
                  >
                    {column.id == "checkbox"
                      ? column.render("Header")
                      : t(column.render("Header"))}
                    <div className="minWidh">
                      {column.canFilter ? column.render("Filter") : ""}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()} style={{ backgroundColor: "#fff" }}>
            {
              page.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="text-muted">
                    {t("no-data-available")}
                  </td>
                </tr>
              )
            }
            {page.map((row: any, i: number) => {
              prepareRow(row);

              return (
                <>
                  <tr
                    {...row.getRowProps()}
                    className={`${assignRelatedClass ? assignRelatedClass(row.cells) : ""
                      } ${i % 2 == 0 ? "even-row" : "odd-row"}`}
                  >
                    {row.cells.map((cell: any, index: number) => {
                      return (
                        <td
                          {...cell.getCellProps()}
                          className={`${cell.column.id === ActionsId ? "sticky" : ""
                            } ${cell?.column.visible === false ? "d-none" : ""}`}
                        >
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                  {row.isExpanded && renderRowSubComponent ? (
                    <tr>
                      <td colSpan={row.cells.length}>
                        {renderRowSubComponent({ row })}
                      </td>
                    </tr>
                  ) : (
                    ""
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>

      {isPagination && TotalRows && (paginationPages && paginationPages.length > 1) ? (
        <div className={`tableFooter ${i18n.language} `}>
          <Icon
            icon="ic:round-chevron-right"
            className={`pagginationArrow ${canPreviousPage ? "" : "disabled"} `}
            onClick={() => (canPreviousPage ? handlePrevPage() : {})}
          />
          <div className="pageCardsContainer">
            <div
              className={`${tableFiller?.page === 0 ? "selectedPage" : ""
                } PageNumCard`}
              onClick={() => handleGoToPage(0)}
            >
              1
            </div>
            {paginationPages
              ?.slice(
                paginationPages?.length == tableFiller?.page
                  ? tableFiller?.page - (PaggingScale - 2)
                  : tableFiller?.page - 1 > 0
                    ? tableFiller?.page - 1
                    : 0,
                tableFiller?.page + PaggingScale
              )
              .map((p) => {
                return (
                  <div
                    key={p}
                    className={`${tableFiller?.page === p - 1 ? "selectedPage" : ""
                      } PageNumCard`}
                    onClick={() => handleGoToPage(p - 1)}
                  >
                    {p}
                  </div>
                );
              })}
            {paginationPages?.length ? (
              <>
                {tableFiller?.page <= paginationPages.length - (PaggingScale + 2) ? (
                  <div>{"..."}</div>
                ) : (
                  ""
                )}
                {tableFiller?.page <= paginationPages.length - (PaggingScale + 1) ? (
                  <div
                    className={`${tableFiller?.page == paginationPages.length
                      ? "selectedPage"
                      : ""
                      } PageNumCard`}
                    onClick={() => handleGoToPage(paginationPages.length)}
                  >
                    {paginationPages.slice(-1)[0]}
                  </div>
                ) : (
                  ""
                )}
              </>
            ) : (
              ""
            )}
          </div>
          <Icon
            icon="ic:round-chevron-left"
            className={`pagginationArrow ${canNextPage ? "" : "disabled"} `}
            onClick={() => (canNextPage ? handleNextPage() : {})}
          />
        </div>
      ) : (
        ""
      )}
    </div>
  );
};

export default memo(Table);
